import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Form,
  Input,
  Space,
  Switch,
  Modal,
  message,
  Card,
  Grid,
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../../api/axios';

const { useBreakpoint } = Grid;

const AddShop = () => {
  const [form] = Form.useForm();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingShop, setEditingShop] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  // === NEW STATES FOR SHOP INFO ===
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const [infoForm] = Form.useForm();
  const [selectedShop, setSelectedShop] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const screens = useBreakpoint();

  // ====================== FETCH SHOPS ======================
  const fetchShops = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/shops');
      setShops(res.data);
    } catch (err) {
      messageApi.error('Failed to fetch shops');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  // ====================== ADD SHOP ======================
  const handleAddShop = async (values) => {
    try {
      await api.post('/api/shops', values);
      messageApi.success('✅ Shop added successfully!');
      form.resetFields();
      fetchShops();
    } catch (error) {
      console.error(error);
      messageApi.error(error.response?.data?.error || 'Failed to add shop');
    }
  };

  // ====================== EDIT SHOP ======================
  const handleEdit = (shop) => {
    setEditingShop(shop);
    setIsEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      await api.put(`/api/shops/${editingShop.id}`, values);
      messageApi.success('✅ Shop updated successfully!');
      setIsEditModalVisible(false);
      fetchShops();
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to update shop');
    }
  };

  // ====================== TOGGLE ACTIVE ======================
  const toggleActive = async (id) => {
    try {
      await api.patch(`/api/shops/${id}/toggle`);
      messageApi.success('Shop status updated');
      fetchShops();
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to update shop status');
    }
  };

  // ====================== ADD / EDIT SHOP INFO ======================
  const handleAddInfoClick = async (shop) => {
    try {
      setSelectedShop(shop);
      infoForm.resetFields();
      setIsInfoModalVisible(true);

      // ✅ Fetch existing info (if available)
      const res = await api.get(`/api/shops/${shop.id}/info`);
      if (res.data) {
        infoForm.setFieldsValue(res.data);
      }
    } catch (err) {
      // It's fine if there's no existing info (404)
      if (err.response?.status !== 404) {
        console.error(err);
        messageApi.error('Failed to load shop info');
      }
    }
  };

  const handleInfoSubmit = async (values) => {
    try {
      await api.post('/api/shops/info', { ...values, shopId: selectedShop.id });
      messageApi.success('Shop Info saved successfully!');
      setIsInfoModalVisible(false);
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to save shop info');
    }
  };

  // ====================== TABLE COLUMNS ======================
  const columns = [
    {
      title: 'Shop Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      responsive: ['md'],
    },
    {
      title: 'Status',
      key: 'isActive',
      render: (_, record) => (
        <Switch
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          checked={record.isActive}
          onChange={() => toggleActive(record.id)}
        />
      ),
      responsive: ['sm'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space direction={screens.xs ? 'vertical' : 'horizontal'}>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            icon={<PlusOutlined />}
            type="link"
            onClick={() => handleAddInfoClick(record)}
          >
            Add/Edit Info
          </Button>
        </Space>
      ),
    },
  ];

  // ====================== RENDER ======================
  return (
    <div style={{ padding: screens.xs ? '0 8px' : '0 24px' }}>
      {contextHolder}

      {/* Add New Shop */}
      <Card
        title="Add New Shop"
        bordered={false}
        style={{
          marginBottom: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        }}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchShops}
            loading={loading}
          >
            Refresh
          </Button>
        }
      >
        <Form
          form={form}
          layout={screens.xs ? 'vertical' : 'inline'}
          onFinish={handleAddShop}
        >
          <Form.Item
            name="name"
            rules={[{ required: true, message: 'Please enter shop name' }]}
          >
            <Input placeholder="Shop Name" />
          </Form.Item>

          <Form.Item
            name="phone"
            rules={[{ required: true, message: 'Please enter phone number' }]}
          >
            <Input placeholder="Phone Number" />
          </Form.Item>

          <Form.Item
            name="address"
            rules={[{ required: true, message: 'Please enter address' }]}
          >
            <Input placeholder="Address" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} block={screens.xs}>
              Add Shop
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Shop List */}
      <Card
        title="Shops List"
        bordered={false}
        style={{
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        }}
      >
        <Table
          columns={columns}
          dataSource={shops}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 6 }}
          scroll={{ x: 'max-content' }}
          size={screens.xs ? 'small' : 'middle'}
        />
      </Card>

      {/* Edit Shop Modal */}
      <Modal
        title="Edit Shop"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          initialValues={editingShop}
          onFinish={handleEditSubmit}
        >
          <Form.Item
            name="name"
            label="Shop Name"
            rules={[{ required: true, message: 'Please enter shop name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: 'Please enter phone number' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Please enter address' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Update Shop
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add / Edit Shop Info Modal */}
      <Modal
        title={`Shop Info - ${selectedShop?.name || ''}`}
        open={isInfoModalVisible}
        onCancel={() => setIsInfoModalVisible(false)}
        footer={null}
        width={screens.xs ? '100%' : 600}
        style={{ top: screens.xs ? 20 : 100 }}
      >
        <Form layout="vertical" form={infoForm} onFinish={handleInfoSubmit}>
          <h4 style={{ marginTop: 0 }}>Headers</h4>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Form.Item key={`header${i}`} name={`header${i}`} label={`Header ${i}`}>
              <Input placeholder={`Header ${i}`} />
            </Form.Item>
          ))}

          <h4>Footers</h4>
          {[1, 2, 3].map((i) => (
            <Form.Item key={`footer${i}`} name={`footer${i}`} label={`Footer ${i}`}>
              <Input placeholder={`Footer ${i}`} />
            </Form.Item>
          ))}

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Save Info
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AddShop;
